import { useEffect, useState } from "react";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { StoreButtons } from "../StoreButtons";

const INTENTS = ["Learn something.", "Teach something.", "Explore somewhere.", "Join something.", "Ask someone.", "Create something.", "Make life happen."];

const useTypewriter = () => {
  const reduce = useReducedMotion();
  const [phrase, setPhrase] = useState(0);
  const [length, setLength] = useState(reduce ? INTENTS[0].length : 0);
  useEffect(() => {
    if (reduce) return undefined;
    const text = INTENTS[phrase];
    const delay = length === text.length ? 1300 : 54;
    const timer = setTimeout(() => {
      if (length === text.length) {
        setLength(0);
        setPhrase((value) => (value + 1) % INTENTS.length);
      } else setLength((value) => value + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [length, phrase, reduce]);
  return INTENTS[phrase].slice(0, length);
};

export const V2Hero = () => {
  const typed = useTypewriter();
  return (
    <section className="v2-hero v2-dots" data-testid="v2-hero">
      <div className="v2-hero-wash" aria-hidden="true" />
      {[1, 3, 6].map((index) => <motion.img key={index} src={`/v2/profiles/profile-0${index}.webp`} alt="" aria-hidden="true" className={`v2-hero-face v2-hero-face-${index}`} animate={{ y: [0, -10, 0] }} transition={{ duration: 5 + index, repeat: Infinity, ease: "easeInOut" }} />)}
      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-36 pb-14 min-h-[100svh] flex flex-col justify-between">
        <div className="max-w-5xl mt-auto mb-auto">
          <motion.img initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} src="/brand/humn-mark.png" alt="HUMN" className="w-16 sm:w-20 h-auto mb-7" data-testid="v2-hero-logo" />
          <h1 className="v2-display" data-testid="v2-hero-title">The <span>Ai</span> Social Network</h1>
          <p className="v2-hero-copy" data-testid="v2-hero-copy">Tell HUMN what you want to make happen.<br />Meet the right people. Turn intentions into actions.</p>
          <div className="v2-hero-action-row">
            <div><div className="v2-intent-input" data-testid="v2-hero-typewriter"><Sparkles size={18} aria-hidden="true" /><span>{typed}</span><i /></div><Link to="/experience" className="v2-text-link mt-5" data-testid="hero-experience-link">Experience HUMN <ArrowUpRight size={16} /></Link></div>
            <StoreButtons placement="hero" />
          </div>
        </div>
        <a href="#the-shift" className="v2-scroll-cue" data-testid="hero-scroll-link"><ArrowDown size={16} /> Scroll into possibility</a>
      </div>
    </section>
  );
};

export const TheShift = () => (
  <section id="the-shift" className="v2-section v2-shift" data-testid="the-shift-section">
    <div className="v2-shell">
      <p className="v2-kicker">01 · THE SHIFT</p>
      <div className="v2-shift-grid">
        <div><p className="v2-faded-word">ATTENTION</p><p className="v2-small-copy">Social networking was built around what everyone else is doing.</p></div>
        <motion.div className="v2-shift-arrow" whileInView={{ x: [0, 10, 0] }} transition={{ repeat: 2, duration: 1 }} aria-hidden="true">→</motion.div>
        <div><p className="v2-live-word">INTENTION</p><p className="v2-small-copy">HUMN begins with what <strong>you</strong> want to make happen.</p></div>
      </div>
    </div>
  </section>
);

const ACTIONS = ["Learn", "Teach", "Ask", "Join", "Explore", "Connect", "Book", "Subscribe", "Order", "Create"];

export const IntentionField = () => (
  <section className="v2-section v2-intentions v2-dots" data-testid="intention-field-section">
    <div className="v2-shell text-center">
      <p className="v2-kicker">02 · BEGIN WITH YOU</p>
      <h2 className="v2-heading">What do you want<br />to make happen?</h2>
      <div className="v2-action-cloud" data-testid="intention-action-cloud">
        {ACTIONS.map((action, index) => <motion.span key={action} initial={{ opacity: 0, scale: .8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: index * .045 }} viewport={{ once: true }}>{action}</motion.span>)}
      </div>
    </div>
  </section>
);

const STEPS = [["01", "Tell HUMN", "Say what you want."], ["02", "HUMN understands", "Intent becomes context."], ["03", "Discover people", "Possibility becomes human."], ["04", "Take action", "Connect, learn, join, book."], ["05", "Make it happen", "Action becomes life."]];

export const HowHumnWorks = () => (
  <section id="how-it-works" className="v2-section" data-testid="how-it-works-section">
    <div className="v2-shell">
      <p className="v2-kicker">03 · HOW HUMN WORKS</p>
      <h2 className="v2-heading max-w-4xl">One intention.<br />A world that moves for you.</h2>
      <div className="v2-process" data-testid="humn-process">
        {STEPS.map(([number, title, copy], index) => <motion.div key={title} className="v2-process-step" initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * .08 }} viewport={{ once: true }}><span>{number}</span><strong>{title}</strong><p>{copy}</p></motion.div>)}
      </div>
    </div>
  </section>
);

export const ExperienceTeaser = () => (
  <section className="v2-experience-teaser" data-testid="experience-teaser-section">
    <div className="v2-experience-track" aria-hidden="true">
      {["ai-chat", "discovery", "rich-slm"].map((name, index) => <motion.img key={name} src={`/app-screens/${name}.jpg`} alt="" initial={{ y: 80 + index * 20 }} whileInView={{ y: 0 }} transition={{ duration: .8, delay: index * .1 }} viewport={{ once: true }} />)}
    </div>
    <div className="v2-shell relative z-10 py-28 sm:py-40">
      <p className="v2-kicker text-white/45">05 · IMMERSIVE HUMN</p>
      <h2 className="v2-heading text-white max-w-3xl">Don't read about HUMN.<br /><span className="font-serif-it font-normal">Travel through it.</span></h2>
      <Link to="/experience" className="v2-enter-btn" data-testid="experience-teaser-link">Enter HUMN <ArrowUpRight /></Link>
    </div>
  </section>
);